import _ from 'lodash'
import {
  applyPageStyles,
  combineStyles
} from '@/utils/useStylesheet'
import router from '@builder/router/index'

const state = () => ({
  loading: false,
  isAvailable: false,
  lastBackupAt: null,
  backups: [],
  versionBackups: [],
  masterBackups: []
})

const actions = {
  async getBackupsList ({state, rootState}) {
    state.loading = true
    try {
      const {data} = await axios.get(`api/projects/${rootState.projects.project.id}/backups`)
      state.backups = data.data
    } catch (error) {
      console.error('Error fetching backups:', error)
    } finally {
      state.loading = false
    }
  },
  async getBackup ({state, rootState}, id) {
    state.loading = true
    try {
      const {data} = await axios.get(`api/projects/${rootState.projects.project.id}/backups/${id}`)
      return data.data
    } catch (error) {
      console.error(`Error fetching backup ${id}:`, error)
    } finally {
      state.loading = false
    }
  },
  async downloadBackup ({state, dispatch}, id) {
    state.loading = true
    try {
      const page = await dispatch('getBackup', id)
      const blob = new Blob([JSON.stringify(page)], {type: 'application/json'})
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${page.title}-${id}.json`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error(`Error downloading backup ${id}:`, error)
    } finally {
      state.loading = false
    }
  },
  async restorePage ({state, rootState, dispatch}, payload) {
    state.loading = true
    VEvent.fire('loader', true)
    try {
      const {data} = await axios.post('api/pages/by-hash', {
        hash: [payload.hash]
      })

      const pageBackup = await dispatch('getBackup', payload.id)

      if (!data.data.length && !pageBackup) {
        return
      }

      const clearContent = (content, safeMode = false) => {
        if (!content) {
          return '[]'
        }

        let contentDraft = content

        contentDraft = contentDraft.replace(/"id":\d+,?/g, '')
          .replace(/"form_id":\d+,?/g, '')

        if (!safeMode) {
          contentDraft = contentDraft.replace(/"action":".*?",?/g, '')
            .replace(/"customLink":".*?",?/g, '')
        }

        contentDraft = contentDraft.replace(/"actions":\[.*?\](,?)/g, '"actions": []$1')
          .replace(/"action":\{.*?\}(,?)/g, '"action": null$1')

        return contentDraft.replace(/,\}/g, '}')
      }

      let pages = [], original_ids = []

      for (const page of data.data) {
        if (!original_ids.includes(page.project_id)) {
          original_ids.push(page.project_id)

          const pageSettings = JSON.parse(page.project.global_settings).font.addedGoogleFonts
          const fonts = _.map(rootState.editor.globalStyles.font.addedGoogleFonts, item1 => {
            const match = _.find(pageSettings, { family: item1.family })
            if (match) {
              return {
                family: item1.family,
                variants: _.uniq(_.concat(item1.variants, match.variants)).sort((a, b) => a - b).map(weight => weight.toString())
              }
            }
            return item1
          })
          pageSettings.forEach(font => {
            const match = _.find(fonts, { family: font.family })
            if (!match) {
              fonts.push({
                family: font.family,
                variants: font.variants
              })
            }
          })

          rootState.editor.globalStyles.font.addedGoogleFonts = fonts
        }

        const _page = {
          slug: pageBackup.slug,
          title: pageBackup.title,
          is_home: false,
          meta_title: pageBackup.meta_title || pageBackup.title,
          page_status: 'DRAFT',
          content_draft: clearContent(pageBackup.content_draft),
          content: clearContent(pageBackup.content),
          code_injection: pageBackup.code_injection || page.code_injection,
          settings: pageBackup.settings,
          css_file: pageBackup.css_file,
          css_file_draft: pageBackup.css_file_draft,
          css_hash: pageBackup.new_hash,
          template_id: pageBackup.template_id || page.template_id
        }

        if (page.modals) {
          _page.modals = page.modals
        }

        pages.push(_page)

        const pageCssUrl = pageBackup.css_file_draft ? pageBackup.css_file_draft : pageBackup.css_file
        let pageCss = await fetch(pageCssUrl).then(res => res.text())
        let extraCss

        if (rootState.projects.project.global_css_draft) {
          extraCss = await fetch(rootState.projects.project.global_css_draft).then(res => res.text())
        }

        const outputCss = combineStyles(pageCss, extraCss)

        applyPageStyles(outputCss, 'sty')
      }

      const {data: createdPages} = await axios.post(`api/projects/${rootState.projects.project.id}/pages/mass-creation?backup=1`, {
        original_ids,
        pages
      })

      await dispatch('pages/loadPage', createdPages.data[0].id, {root: true})
      await dispatch('pages/save', {clearCache: 1, live: true}, {root: true})

      await router.push({
        name: 'editor',
        params: {id: rootState.projects.project.id},
        query: {page: createdPages.data[0].id}
      })

      VEvent.fire('loader', false)
    } catch (error) {
      VEvent.fire('loader', false)
      console.error('Error restoring page:', error)
    } finally {
      state.loading = false
    }
  },
  async restorePageByFile ({state, rootState, dispatch}, payload) {
    state.loading = true
    VEvent.fire('loader', true)
    try {
      if (!payload) {
        return
      }

      const page = payload

      let pages = [], original_ids = []

      original_ids.push(page.project_id)

      if (page?.project?.global_settings) {
        const pageSettings = JSON.parse(page.project.global_settings).font.addedGoogleFonts
        const fonts = _.map(rootState.editor.globalStyles.font.addedGoogleFonts, item1 => {
          const match = _.find(pageSettings, { family: item1.family })
          if (match) {
            return {
              family: item1.family,
              variants: _.uniq(_.concat(item1.variants, match.variants)).sort((a, b) => a - b).map(weight => weight.toString())
            }
          }
          return item1
        })
        pageSettings.forEach(font => {
          const match = _.find(fonts, { family: font.family })
          if (!match) {
            fonts.push({
              family: font.family,
              variants: font.variants
            })
          }
        })

        rootState.editor.globalStyles.font.addedGoogleFonts = fonts
      }

      const _page = {
        slug: page.slug,
        title: page.title,
        is_home: false,
        meta_title: page.title,
        page_status: 'DRAFT',
        content_draft: page.content_draft,
        content: page.content,
        code_injection: page.code_injection,
        settings: page.settings,
        css_file: page.css_file,
        css_file_draft: page.css_file_draft,
        css_hash: page.css_hash,
        template_id: page.template_id
      }

      if (page.modals) {
        _page.modals = page.modals
      }

      pages.push(_page)

      const pageCssUrl = page.css_file_draft ? page.css_file_draft : page.css_file
      let pageCss = await fetch(pageCssUrl).then(res => res.text())
      let extraCss

      if (rootState.projects.project.global_css_draft) {
        extraCss = await fetch(rootState.projects.project.global_css_draft).then(res => res.text())
      }

      const outputCss = combineStyles(pageCss, extraCss)

      applyPageStyles(outputCss, 'sty')

      const {data: createdPages} = await axios.post(`api/projects/${rootState.projects.project.id}/pages/mass-creation?backup=1`, {
        pages
      })

      await dispatch('pages/loadPage', createdPages.data[0].id, {root: true})
      // await dispatch('pages/save', {clearCache: 1, live: true}, {root: true})

      VEvent.fire('loader', false)
    } catch (error) {
      VEvent.fire('loader', false)
      console.error('Error restoring page:', error)
    } finally {
      state.loading = false
    }
  },
  async getVersionBackupsList ({state, rootState}) {
    state.loading = true
    try {
      const {data} = await axios.get(`api/projects/${rootState.projects.project.id}/backups/version`)
      state.versionBackups = data.data
    } catch (error) {
      console.error('Error fetching version backups:', error)
    } finally {
      state.loading = false
    }
  },
  async postVersionBackup ({state, rootState}, payload) {
    state.loading = true
    try {
      await axios.post(`api/projects/${rootState.projects.project.id}/backups/version`, payload)
    } catch (error) {
      console.error('Error creating version backup:', error)
    } finally {
      state.loading = false
    }
  },
  async postVersionBackupList ({state, rootState}, payload) {
    state.loading = true
    try {
      if (payload.length === 0) return

      for (const version of payload) {
        const {data} = await axios.get(`api/projects/${rootState.projects.project.id}/pages/${version}`)

        const versionBackup = {
          original_id: data.data.id,
          slug: data.data.slug,
          title: data.data.title,
          content: data.data.content,
          content_draft: data.data.content_draft,
          settings: data.data.settings,
          fonts: data.data.fonts,
          css_hash: data.data.css_hash,
          css_file: data.data.css_file,
          css_file_draft: data.data.css_file_draft,
          hash: data.data.hash,
          original_css: rootState.projects.project.css_file_draft,
          page_status: 'DRAFT',
          project_id: rootState.projects.project.id
        }

        await axios.post(`api/projects/${rootState.projects.project.id}/backups/version`, versionBackup)
      }
    } catch (error) {
      console.error('Error creating version backup:', error)
    } finally {
      state.loading = false
    }
  },
  async getMasterBackups ({rootState, state}) {
    state.loading = true
    try {
      const {data} = await axios.get(`api/projects/${rootState.projects.project.id}/backups/master-backup`)
      state.masterBackups = data.data
    } catch (error) {
      console.error('Error fetching master backups:', error)
    } finally {
      state.loading = false
    }
  },
  async postMasterBackups ({rootState, state, dispatch}) {
    state.loading = true
    try {
      await axios.post(`api/projects/${rootState.projects.project.id}/backups/master-backup`)
      await dispatch('checkIsAvailable')
      await this.$swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Project backup completed successfully',
        showConfirmButton: false,
        timer: 2500
      })

      setTimeout(async () => await dispatch('getMasterBackups'), 1500)
    } catch (error) {
      console.error('Error post master backups:', error)
    } finally {
      state.loading = false
    }
  },
  async restoreMasterBackups ({rootState, state}, id) {
    state.loading = true
    try {
      await axios.get(`api/projects/${rootState.projects.project.id}/backups/restore-master-backup/${id}`)
    } catch (error) {
      console.error('Error restoring master backups:', error)
    } finally {
      state.loading = false
    }
  },
  async checkIsAvailable ({rootState, state}) {
    state.loading = true
    try {
      const {data} = await axios.get(`api/projects/${rootState.projects.project.id}/backups/check-is-available`)
      state.isAvailable = data.data
      return data.data
    } catch (error) {
      const status = error?.response?.status
      const responseData = error?.response?.data
      const message = responseData?.message || error?.message || String(error)

      state.lastBackupAt = responseData?.data?.last_backup_at

      console.error('Error checking backups availability:', {
        message,
        status,
        responseData
      })

      state.isAvailable = false

      return null
    } finally {
      state.loading = false
    }
  },
  async downloadMasterBackup ({state, rootState}, file) {
    state.loading = true
    try {
      const a = document.createElement('a')
      a.href = file
      a.download = `${rootState.projects.project.name}.json`
      a.click()
      window.URL.revokeObjectURL(file)
    } catch (error) {
      console.error('Error downloading backup:', error)
    } finally {
      state.loading = false
    }
  }
}

export default {
  namespaced: true,
  state,
  actions
}